import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, FormControlLabel, Paper, Slider, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Context } from '../../../App';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import SeoHelmet from '../../common/SeoHelmet';
import MuiSnackbar from '../../common/MuiSnackbar';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import MohrCircleSketch from './MohrCircleSketch';
import { computeEffectiveInputs, computeMohrState, formatValue, normalizeThetaDeg } from './mohrCircleMath';

const STRESS_LIMIT = 1e6;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const labelWithSubscript = (symbol, subscript, suffix = '') => (
  <>
    {symbol}
    <sub>{subscript}</sub>
    {suffix}
  </>
);

const buildCards = (state) => {
  if (!state) return [];
  return [
    { id: 'sigma1', label: labelWithSubscript('σ', '1', ' (kPa)'), value: formatValue(state.sigma1, 3) },
    { id: 'sigma3', label: labelWithSubscript('σ', '3', ' (kPa)'), value: formatValue(state.sigma3, 3) },
    { id: 'tauMax', label: labelWithSubscript('τ', 'max', ' (kPa)'), value: formatValue(state.tauMax, 3) },
    {
      id: 'centerSigma',
      label: <>Center C = {labelWithSubscript('σ', 'avg')} (kPa)</>,
      value: formatValue(state.centerSigma, 3),
    },
    { id: 'radius', label: 'Radius R (kPa)', value: formatValue(state.radius, 3) },
    { id: 'thetaP', label: labelWithSubscript('θ', 'p', ', CW (deg)'), value: formatValue(state.thetaPrincipalDeg, 3) },
    { id: 'thetaS', label: labelWithSubscript('θ', 's', ', CW (deg)'), value: formatValue(state.thetaMaxShearDeg, 3) },
    { id: 'sigmaTheta', label: 'σ(θ) (kPa)', value: formatValue(state.points?.selected?.sigma, 3) },
    { id: 'tauTheta', label: 'τ(θ) (kPa)', value: formatValue(state.points?.selected?.tau, 3) },
  ];
};

const labelWithHelp = (labelText, helpText) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.375,
      px: 0.35,
      py: 0.05,
      mr: 0.25,
      borderRadius: 0.5,
      backgroundColor: 'background.paper',
      whiteSpace: 'nowrap',
      lineHeight: 1.1,
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Box component="span" sx={{ lineHeight: 1.1 }}>
      {labelText}
    </Box>
    <Tooltip title={helpText} arrow placement="top" enterTouchDelay={0}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
      </Box>
    </Tooltip>
  </Box>
);

const ResultBlock = ({ title, state }) => {
  const cards = buildCards(state);
  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {cards.map((card) => (
          <Paper key={`${title}-${card.id}`} variant="outlined" sx={{ p: 1.2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {card.label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Stack>
  );
};

const MohrCircle = () => {
  const { theme: appTheme } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  const [name, setName] = useState('Case-01');
  const [sigmaX, setSigmaX] = useState('120');
  const [sigmaY, setSigmaY] = useState('60');
  const [tauXY, setTauXY] = useState('35');
  const [thetaDeg, setThetaDeg] = useState('20');
  const [useEffectiveStress, setUseEffectiveStress] = useState(false);
  const [porePressure, setPorePressure] = useState('0');

  const URL = {
    CALCULATE: '/api/v1/calculations/mohrCircle',
    LOADONE: '/api/v1/calculations/mohrCircle/loadOne/',
    LOADALL: '/api/v1/calculations/mohrCircle/loadAll',
    UPDATE: '/api/v1/calculations/mohrCircle/update/',
    SAVE: '/api/v1/calculations/mohrCircle/save',
    CHECKUNIQUENAME: '/api/v1/calculations/mohrCircle/checkUniqueName/',
    DELETE: '/api/v1/calculations/mohrCircle/delete',
  };

  const setInputs = (loadedInput) => {
    setName(String(loadedInput?.name ?? ''));
    setSigmaX(String(loadedInput?.sigmaX ?? ''));
    setSigmaY(String(loadedInput?.sigmaY ?? ''));
    setTauXY(String(loadedInput?.tauXY ?? ''));
    setThetaDeg(String(loadedInput?.thetaDeg ?? '0'));
    setUseEffectiveStress(Boolean(loadedInput?.useEffectiveStress));
    setPorePressure(String(loadedInput?.porePressure ?? '0'));
  };

  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const normalizedTheta = normalizeThetaDeg(thetaDeg);
  const numberInputProps = useMemo(
    () => ({
      style: { colorScheme: appTheme === 'dark' ? 'dark' : 'light' },
    }),
    [appTheme],
  );

  const calculationInput = useMemo(
    () => ({
      name: name.trim(),
      sigmaX: toNumber(sigmaX),
      sigmaY: toNumber(sigmaY),
      tauXY: toNumber(tauXY),
      thetaDeg: normalizedTheta,
      useEffectiveStress: Boolean(useEffectiveStress),
      porePressure: useEffectiveStress ? toNumber(porePressure) : 0,
    }),
    [name, sigmaX, sigmaY, tauXY, normalizedTheta, useEffectiveStress, porePressure],
  );

  const validationErrors = useMemo(() => {
    const errors = [];

    if (!calculationInput.name) errors.push('Name is required.');
    if (calculationInput.name.length > MAX_NAME_LENGTH) {
      errors.push(`Name must be ${MAX_NAME_LENGTH} characters or less.`);
    }

    if (!Number.isFinite(calculationInput.sigmaX)) errors.push('sigmaX must be a finite number.');
    if (!Number.isFinite(calculationInput.sigmaY)) errors.push('sigmaY must be a finite number.');
    if (!Number.isFinite(calculationInput.tauXY)) errors.push('tauXY must be a finite number.');
    if (!Number.isFinite(calculationInput.thetaDeg)) errors.push('theta must be a finite number.');

    if (Number.isFinite(calculationInput.sigmaX) && Math.abs(calculationInput.sigmaX) > STRESS_LIMIT) {
      errors.push(`sigmaX magnitude must be <= ${STRESS_LIMIT}.`);
    }
    if (Number.isFinite(calculationInput.sigmaY) && Math.abs(calculationInput.sigmaY) > STRESS_LIMIT) {
      errors.push(`sigmaY magnitude must be <= ${STRESS_LIMIT}.`);
    }
    if (Number.isFinite(calculationInput.tauXY) && Math.abs(calculationInput.tauXY) > STRESS_LIMIT) {
      errors.push(`tauXY magnitude must be <= ${STRESS_LIMIT}.`);
    }

    if (calculationInput.useEffectiveStress) {
      if (!Number.isFinite(calculationInput.porePressure)) {
        errors.push('Pore pressure must be a finite number in effective stress mode.');
      }
      if (Number.isFinite(calculationInput.porePressure) && calculationInput.porePressure < 0) {
        errors.push('Pore pressure cannot be negative.');
      }
    }

    return errors;
  }, [MAX_NAME_LENGTH, calculationInput]);

  useEffect(() => {
    const hasErrors = validationErrors.length > 0;
    setCalculateButtonDisabled(hasErrors);
    setSaveButtonDisabled(hasErrors);
  }, [setCalculateButtonDisabled, setSaveButtonDisabled, validationErrors]);

  useEffect(() => {
    setShowOutput(false);
  }, [setShowOutput, sigmaX, sigmaY, tauXY, thetaDeg, useEffectiveStress, porePressure]);

  const createNewCalculation = () => {
    setName('Case-01');
    setSigmaX('120');
    setSigmaY('60');
    setTauXY('35');
    setThetaDeg('20');
    setUseEffectiveStress(false);
    setPorePressure('0');
    setShowOutput(false);
  };

  const totalPreviewState = useMemo(
    () =>
      computeMohrState({
        sigmaX: toNumber(sigmaX),
        sigmaY: toNumber(sigmaY),
        tauXY: toNumber(tauXY),
        thetaDeg: normalizedTheta,
      }),
    [sigmaX, sigmaY, tauXY, normalizedTheta],
  );

  const effectivePreviewState = useMemo(() => {
    if (!useEffectiveStress) return null;
    const effectiveInputs = computeEffectiveInputs({
      sigmaX: toNumber(sigmaX),
      sigmaY: toNumber(sigmaY),
      tauXY: toNumber(tauXY),
      thetaDeg: normalizedTheta,
      porePressure: toNumber(porePressure),
    });
    if (!effectiveInputs) return null;
    return computeMohrState(effectiveInputs);
  }, [useEffectiveStress, sigmaX, sigmaY, tauXY, normalizedTheta, porePressure]);

  const previewState = useEffectiveStress ? effectivePreviewState : totalPreviewState;
  const previewTitle = useEffectiveStress ? 'Effective Stress Circle Preview' : 'Total Stress Circle Preview';

  const output = showOutput ? result?.outputs : null;
  const totalOutput = output?.total;
  const effectiveOutput = output?.effective;

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Interactive Mohr Circle"
        description="Interactive Mohr Circle for plane stress transformation: principal stresses, maximum shear stress, and stress on a selected plane with clear engineering conventions."
        url="https://www.geocalc.app/calculations/mohr-circle"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Interactive Mohr Circle', url: 'https://www.geocalc.app/calculations/mohr-circle' },
        ]}
      />
      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon color="primary" fontSize="large" className="sidebar-open-icon" onClick={handleSidebarDisplay} />
        )}

        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />

        <div className="right-calculation">
          <h1>Interactive Mohr Circle</h1>
          <Stack spacing={2} sx={{ p: 1 }}>
            {validationErrors.length > 0 && <Alert severity="warning">{validationErrors[0]}</Alert>}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
              <Paper
                variant="outlined"
                sx={{
                  width: { xs: '100%', lg: 420 },
                  flex: { xs: '1 1 auto', lg: '0 0 420px' },
                  minWidth: 0,
                  p: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    label={labelWithHelp('Name', `Case name used for save/load. Maximum ${MAX_NAME_LENGTH} characters.`)}
                    value={name}
                    onChange={(e) => {
                      if (e.target.value.length > MAX_NAME_LENGTH) {
                        showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
                      }
                      setName(e.target.value);
                    }}
                    inputProps={{ maxLength: MAX_NAME_LENGTH }}
                    disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    fullWidth
                  />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      size="small"
                      label={labelWithHelp(
                        <>
                          &sigma;<sub>x</sub> (kPa)
                        </>,
                        'Normal stress acting on the x-face. Compression is positive.',
                      )}
                      type="number"
                      inputProps={numberInputProps}
                      value={sigmaX}
                      onChange={(e) => setSigmaX(e.target.value)}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                    <TextField
                      size="small"
                      label={labelWithHelp(
                        <>
                          &sigma;<sub>y</sub> (kPa)
                        </>,
                        'Normal stress acting on the y-face. Compression is positive.',
                      )}
                      type="number"
                      inputProps={numberInputProps}
                      value={sigmaY}
                      onChange={(e) => setSigmaY(e.target.value)}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                    <TextField
                      size="small"
                      label={labelWithHelp(
                        <>
                          &tau;<sub>xy</sub> (kPa)
                        </>,
                        'Positive τxy is plotted downward on the Mohr circle and corresponds to CCW shear sense.',
                      )}
                      type="number"
                      inputProps={numberInputProps}
                      value={tauXY}
                      onChange={(e) => setTauXY(e.target.value)}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                    <TextField
                      size="small"
                      label={labelWithHelp(
                        <>&theta; (deg)</>,
                        'Plane orientation angle measured clockwise from x-face. Mohr-circle angle is 2θ.',
                      )}
                      type="number"
                      inputProps={numberInputProps}
                      value={thetaDeg}
                      onChange={(e) => setThetaDeg(e.target.value)}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                  </Box>

                  <Stack spacing={1.25} sx={{ px: 0.5, pt: 0.5 }}>
                    <Box sx={{ color: 'text.secondary', typography: 'caption' }}>
                      {labelWithHelp(
                        <>&theta; selector (0 to 180 deg)</>,
                        'Interactive clockwise angle control from x-face. Valid range is 0 to 180 degrees.',
                      )}
                    </Box>
                    <Slider
                      min={0}
                      max={180}
                      step={1}
                      value={Number.isFinite(toNumber(thetaDeg)) ? clampValue(toNumber(thetaDeg), 0, 180) : 0}
                      onChange={(_, value) => setThetaDeg(String(value))}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                      sx={{ mt: 0.5 }}
                    />
                  </Stack>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={useEffectiveStress}
                        onChange={(e) => setUseEffectiveStress(e.target.checked)}
                        disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                      />
                    }
                    label={labelWithHelp(
                      <>Use effective stress (&sigma;&prime; = &sigma; - u)</>,
                      'When enabled, pore pressure u is subtracted from normal stresses to compute the effective-stress circle.',
                    )}
                  />

                  {useEffectiveStress && (
                    <TextField
                      size="small"
                      label={labelWithHelp(<>u (kPa)</>, 'Pore water pressure used only in effective-stress mode.')}
                      type="number"
                      inputProps={numberInputProps}
                      value={porePressure}
                      onChange={(e) => setPorePressure(e.target.value)}
                      disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                    />
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Sign convention: compression-positive σ; τ positive downward (CCW); θ positive clockwise from x-face, with 2θ
                    on Mohr circle.
                  </Typography>

                  <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ pt: 0.5, width: '100%' }}>
                    <CalculateButton
                      calculationCountReached={calculationCountReached}
                      calculate={calculate}
                      calculationInput={calculationInput}
                      URL={URL}
                      calculateButtonDisabled={calculateButtonDisabled}
                      isLoadingCalculate={isLoadingCalculate}
                      isLoadingLoad={isLoadingLoad}
                      isLoadingSave={isLoadingSave}
                      layersFrontLength={1}
                    />
                    <SaveButton
                      saveCountReached={saveCountReached}
                      checkCalculationNameforSaving={checkCalculationNameforSaving}
                      calculationInput={calculationInput}
                      URL={URL}
                      saveButtonDisabled={saveButtonDisabled}
                      isLoadingCalculate={isLoadingCalculate}
                      isLoadingLoad={isLoadingLoad}
                      isLoadingSave={isLoadingSave}
                      layersFrontLength={1}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
                <MohrCircleSketch state={previewState} title={previewTitle} />
                {previewState && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Preview updates instantly. Press Calculate to persist the exact state and save/load it from the sidebar.
                  </Typography>
                )}
              </Paper>
            </Stack>

            {showOutput && output && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Results</Typography>
                  <ResultBlock title="Total Stress Results" state={totalOutput} />
                  {effectiveOutput && <ResultBlock title="Effective Stress Results" state={effectiveOutput} />}
                </Stack>
              </Paper>
            )}
          </Stack>
        </div>
      </div>
      <Footer />
    </>
  );
};

const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));

const MohrCircleWithProvider = () => (
  <CalculationContextProvider>
    <MohrCircle />
  </CalculationContextProvider>
);

export default MohrCircleWithProvider;
